import { Button } from "@/components/ui/button";
import { FileDown } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { toast } from "sonner";
import cbamLogo from "@/assets/cbam-presentation-logo.png";

interface PostPdfExportButtonProps {
  title: string;
  excerpt?: string | null;
  contentHtml: string;
  author?: string | null;
  publishedAt?: string | null;
  category?: string | null;
  featuredImageUrl?: string | null;
  label?: string;
}

function escapeHtml(unsafe: string): string {
  return (unsafe ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(value?: string | null) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export function PostPdfExportButton({
  title,
  excerpt,
  contentHtml,
  author,
  publishedAt,
  category,
  featuredImageUrl,
  label = "Export PDF",
}: PostPdfExportButtonProps) {
  const exportPdf = () => {
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) {
      // WHY: this was a bare `return`, so a browser that blocked the popup left the
      // button looking dead on every public blog and insights post, with nothing in
      // the UI to tell the reader why. Sonner is mounted in all three app entries
      // (AppPublic/AppAdviser/AppInternal) and the sibling SocialShare rendered on
      // the same row already imports it, so this adds no new dependency.
      toast.error(
        "Could not open the print window. Please allow pop-ups for this site and try again.",
      );
      return;
    }

    const meta = [
      author ? escapeHtml(author) : "",
      formatDate(publishedAt),
      category ? escapeHtml(category) : "",
    ]
      .filter(Boolean)
      .join(" &middot; ");

    const logoUrl = new URL(cbamLogo, window.location.origin).href;
    const generated = formatDate(new Date().toISOString());
    const desc = (excerpt || "").replace(/\s+/g, " ").slice(0, 160);

    w.document.write(`
      <html><head><meta charset="utf-8" /><title>${escapeHtml(title)}</title>
      <meta name="description" content="${escapeHtml(desc)}" />
      <meta name="author" content="${escapeHtml(author || "Cape Berkshire Asset Management")}" />
      <meta name="subject" content="${escapeHtml(category || "")}" />
      <meta name="publisher" content="Cape Berkshire Asset Management" />
      <meta name="copyright" content="© ${new Date().getFullYear()} Cape Berkshire Asset Management" />
      <style>
        body { font-family: Georgia, "Times New Roman", serif; max-width: 760px; margin: 40px auto; padding: 0 28px; color: #1a1a1a; line-height: 1.6; }
        .brand { display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 2px solid #004742; padding-bottom: 14px; margin-bottom: 22px; }
        .brand img { height: 46px; width: auto; margin: 0; border-radius: 0; }
        .brand .brand-meta { font-family: -apple-system, system-ui, sans-serif; font-size: 10px; color: #004742; text-align: right; line-height: 1.5; }
        h1 { font-family: -apple-system, system-ui, sans-serif; color: #004742; font-size: 28px; margin-bottom: 8px; }
        .meta { font-family: -apple-system, system-ui, sans-serif; color: #666; font-size: 12px; border-bottom: 1px solid #004742; padding-bottom: 12px; margin-bottom: 20px; }
        .excerpt { font-size: 16px; color: #333; font-style: italic; margin-bottom: 20px; }
        h2, h3, h4 { font-family: -apple-system, system-ui, sans-serif; color: #004742; }
        img { max-width: 100%; height: auto; border-radius: 6px; margin: 16px 0; }
        blockquote { border-left: 4px solid #004742; padding-left: 14px; margin-left: 0; font-style: italic; color: #333; }
        iframe { display: none; }
        a { color: #004742; }
        .footer { margin-top: 32px; padding-top: 14px; border-top: 1px solid #004742; font-family: -apple-system, system-ui, sans-serif; font-size: 10px; color: #555; line-height: 1.6; }
        @media print { body { margin: 0; max-width: none; } }
      </style></head><body>
      <div class="brand">
        <img src="${escapeHtml(logoUrl)}" alt="Cape Berkshire Asset Management" />
        <div class="brand-meta">
          Cape Berkshire Asset Management<br />
          capeberkshire.co.uk<br />
          Generated ${generated}
        </div>
      </div>
      <h1>${escapeHtml(title)}</h1>
      <div class="meta">${meta}</div>
      ${featuredImageUrl ? `<img src="${escapeHtml(featuredImageUrl)}" alt="${escapeHtml(title)}" />` : ""}
      ${excerpt ? `<div class="excerpt">${escapeHtml(excerpt)}</div>` : ""}
      <div class="content">${sanitizeHtml(contentHtml)}</div>
      <div class="footer">
        Cape Berkshire Asset Management is a trading name of Ascot Wealth Management Ltd, authorised and regulated by the Financial Conduct Authority, FCA Firm Reference Number 551744.<br />
        © ${new Date().getFullYear()} Cape Berkshire Asset Management. This document is for information only and does not constitute advice.
      </div>
      </body></html>
    `);
    w.document.close();
    w.focus();

    // WHY: this used to print on a blind setTimeout(..., 800), which raced the load of
    // the CBAM logo and the featured image. On a cold cache neither had decoded by
    // 800ms, so the client-facing PDF printed unbranded and with an empty image box.
    // Wait for the popup document to finish loading and then for every injected image
    // to actually decode. Each decode() is swallowed so a single broken or
    // cross-origin image cannot hold the print dialog hostage, and the whole wait is
    // raced against a 6s ceiling so a popup that never reaches readyState "complete"
    // can never hang the button. The readyState check and the listener attach sit in
    // the same synchronous block, so the load event cannot slip between them.
    // Does NOT fix: an image that 404s still prints as a broken box, and this does not
    // wait on web fonts (the printed document declares only system and generic
    // families, so there are none to wait for).
    const ready = (async () => {
      if (w.document.readyState !== "complete") {
        await new Promise<void>((resolve) => {
          w.addEventListener("load", () => resolve(), { once: true });
        });
      }
      await Promise.all(
        Array.from(w.document.images).map((img) => img.decode().catch(() => {})),
      );
    })();

    const ceiling = new Promise<void>((resolve) => {
      window.setTimeout(resolve, 6000);
    });

    void Promise.race([ready, ceiling]).then(() => {
      if (w.closed) return;
      w.focus();
      w.print();
    });
  };

  return (
    <Button variant="outline" size="sm" onClick={exportPdf}>
      <FileDown className="h-4 w-4 mr-2" />
      {label}
    </Button>
  );
}

export default PostPdfExportButton;
